import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';

import {
  DataTable,
  DataTableColumn,
  DataTableCell
} from '@salesforce/design-system-react';

import './ErrorList.scss';

const CustomDataTableCell = ({ children, property }) => {
  switch (property) {
    default: {
      return <td className="error-cell wrap-text">{children}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  item: PropTypes.object
};

class ErrorList extends Component {
  headerCell(property, formatMessage) {
    return formatMessage({
      id: `components.ecs.error.${property}`
    });
  }

  columns() {
    return {
      type: { sortable: false },
      line: { sortable: false, width: '10%' },
      context: { sortable: false, width: '45%' }
    };
  }

  tableData(items) {
    const { formatMessage } = this.props.intl;
    return items.map(item => {
      const id = JSON.stringify(item.context);
      let context;
      switch (item.type.toUpperCase()) {
        case 'IMAGE_UNREACHABLE':
          context = item.context.image_url;
          break;
        case 'IMAGE_BAD_FILE_EXT':
          context = formatMessage(
            { id: 'components.ecs.error.badFileExt' },
            {
              file_ext: item.context.file_ext,
              valid: JSON.stringify(item.context.valid, null, 1)
            }
          );
          break;
        case 'EMPTY_COLUMN_NAME':
          context = formatMessage(
            { id: 'components.ecs.error.emptyColumnName' },
            { column: item.context.column }
          );

          break;
        case 'INVALID_ROWS':
          context = JSON.stringify(item.context.fields);
          break;
        case 'INVALID_DATE':
          if (item.context.start_datetime || item.context.start_date) {
            context = formatMessage(
              { id: 'components.ecs.error.invalidStartDate' },
              { date: item.context.start_datetime || item.context.start_date }
            );
          } else {
            context = formatMessage(
              { id: 'components.ecs.error.invalidEndDate' },
              { date: item.context.end_datetime || item.context.end_date }
            );
          }
          break;
        case 'START_AFTER_END':
          context = formatMessage(
            { id: 'components.ecs.error.startAfterEnd' },
            {
              startDate: item.context.start_datetime || item.context.start_date,
              endDate: item.context.end_datetime || item.context.end_date
            }
          );
          break;
        case 'PROFILE_ROW_FAILED':
          context = JSON.stringify(item.context);
          break;
        case 'INVALID_PROFILE_ATTRIBUTES':
          context = formatMessage(
            { id: 'components.ecs.error.notInValueList' },
            {
              subscriber: item.context.subscriber_id,
              attribute: item.context.attribute,
              value: item.context.value
            }
          );
          break;
        case 'MISSING_REQUIRED_COLUMNS':
          context = JSON.stringify(item.context.columns);
          break;
        case 'DUPLICATE_KEY':
          const { AssetName: key, last_line: line } = item.context;
          context = formatMessage(
            { id: 'components.ecs.error.duplicateKey' },
            { key, line }
          );
          break;
        case 'ERROR_PROFILE_IMPORT_MISSED_ROWS':
          context = formatMessage(
            {
              id:
                'components.ecs.error.types.ERROR_PROFILE_IMPORT_MISSED_ROWS.context'
            },
            {
              total: item.context.total,
              imported: item.context.imported,
              missed: item.context.missed
            }
          );
          break;
        case 'CHILD_CLASS_INVALID':
          context = formatMessage(
            { id: 'components.ecs.error.invalidChild' },
            { children: item.context.children }
          );
          break;
        case 'CLASS_MISMATCHED':
          context = formatMessage(
            { id: 'components.ecs.error.wrongParent' },
            {
              asset: item.context.asset,
              parent_class: item.context.parent_class,
              parent: item.context.parent,
              expected_parent_class: item.context.expected_parent_class
            }
          );
          break;
        case 'CLASS_SAME_AS_PARENT':
          context = formatMessage(
            { id: 'components.ecs.error.childasParent' },
            {
              asset: item.context.asset,
              asset_class: item.context.asset_class,
              parent: item.context.parent
            }
          );
          break;
        case 'NESTED_CHILD_ASSET':
          context = formatMessage(
            { id: 'components.ecs.error.noGrandchild' },
            { asset: item.context.asset, asset_class: item.context.asset_class }
          );
          break;
        case 'PARENT_NOT_ALLOWED':
          context = formatMessage(
            { id: 'components.ecs.error.unAllowedParent' },
            { asset: item.context.asset, asset_class: item.context.asset_class }
          );
          break;
        case 'PARENT_NOT_FOUND':
          context = formatMessage(
            { id: 'components.ecs.error.lostParent' },
            { asset: item.context.asset, parent: item.context.parent }
          );
          break;
        case 'PARENT_REQUIRED':
          context = formatMessage(
            { id: 'components.ecs.error.parentMissing' },
            {
              asset: item.context.asset,
              asset_class: item.context.asset_class,
              parent_class: item.context.parent_class
            }
          );
          break;
        case 'SUBJECT_LINE_REQUIRED':
          context = formatMessage(
            { id: 'components.ecs.error.subjectLineRequired' },
            {
              asset: item.context.asset,
              asset_class: item.context.asset_class
            }
          );
          break;
        case 'CLASS_ARCHIVED':
          context = formatMessage(
            { id: 'components.ecs.error.classArchived' },
            {
              asset: item.context.asset,
              asset_class: item.context.asset_class
            }
          );
          break;
        case 'URL_CONTAINS_AMPSCRIPT':
          context = item.context.url;
          break;
        case 'IMAGE_URL_CONTAINS_AMPSCRIPT':
          context = item.context.image_url;
          break;
        default:
          break;
      }

      let type;
      switch (item.type.toUpperCase()) {
        case 'IMPORT_MAX_EXCEEDED':
          type = formatMessage(
            { id: 'components.ecs.error.types.IMPORT_MAX_EXCEEDED' },
            { import_max: item.context.line - 2 }
          );
          break;
        case 'IMPORT_MAX_ATTRIBUTES_EXCEEDED':
          type = formatMessage(
            { id: 'components.ecs.error.types.IMPORT_MAX_ATTRIBUTES_EXCEEDED' },
            {
              total_attributes: item.context.total_attributes,
              max_attributes: item.context.max_attributes
            }
          );
          break;
        default:
          type = formatMessage({
            id: `components.ecs.error.types.${item.type}`
          });
          break;
      }

      return {
        ...item,
        type: type,
        line: item.context.line,
        id,
        context
      };
    });
  }

  render() {
    const { formatMessage } = this.props.intl;
    const items = this.tableData(this.props.errors);
    return (
      <div>
        <DataTable items={items} className="error-list-table" fixedLayout>
          {Object.entries(this.columns()).map((column, index) => {
            const sortable = column[1].sortable;
            const property = column[0];
            const width = column[1].width;
            return (
              <DataTableColumn key={index} sortable={sortable} width={width}>
                <CustomDataTableCell
                  property={property}
                  formatMessage={formatMessage}
                  label={this.headerCell(property, formatMessage)}
                />
              </DataTableColumn>
            );
          })}
        </DataTable>
      </div>
    );
  }
}

ErrorList.propTypes = {
  actions: PropTypes.object,
  errors: PropTypes.array,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(ErrorList);
